import { useEffect, useState } from "react";
import { getAgreementMetrics, getValidationRuns } from "../../services/api";

// Landis & Koch (1977) bands.
const KAPPA_BANDS = [
  { max: 0.0,  color: "#dc2626", label: "Poor" },
  { max: 0.21, color: "#ea580c", label: "Slight" },
  { max: 0.41, color: "#f59e0b", label: "Fair" },
  { max: 0.61, color: "#eab308", label: "Moderate" },
  { max: 0.81, color: "#84cc16", label: "Substantial" },
  { max: 1.01, color: "#16a34a", label: "Almost perfect" },
];

function kappaColor(kappa) {
  if (kappa === null || kappa === undefined) return "#9ca3af";
  return KAPPA_BANDS.find((b) => kappa < b.max)?.color || "#16a34a";
}

const FIELD_LABELS = {
  audio_type: "Audio type",
  interaction_type: "Interaction type",
  environment: "Environment",
  transcript_wrong: "Transcript-wrong flag",
  red_flag: "Inappropriate flag",
};

const CONSENSUS_LABELS = {
  all_agree: "All 3 annotators agree",
  two_agree: "2 of 3 agree",
  all_disagree: "All 3 disagree",
};

const CONSENSUS_COLORS = {
  all_agree: "#16a34a",
  two_agree: "#f59e0b",
  all_disagree: "#dc2626",
};

function KappaBar({ field, info }) {
  const kappa = info.fleiss_kappa;
  const pct = kappa === null ? 0 : Math.max(0, Math.min(100, kappa * 100));
  return (
    <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 110px 100px", alignItems: "center", gap: 12, padding: "8px 0" }}>
      <div style={{ fontWeight: 500, color: "var(--text)" }}>{FIELD_LABELS[field] || field}</div>
      <div style={{ position: "relative", height: 16, background: "rgba(255,255,255,0.04)", borderRadius: 4, overflow: "hidden" }}>
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: kappaColor(kappa),
            transition: "width 0.3s ease",
          }}
        />
      </div>
      <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 13 }}>
        {kappa === null ? "—" : `κ = ${kappa.toFixed(3)}`}
      </div>
      <div style={{ fontSize: 12, color: "var(--muted)" }}>
        n = {info.n_chunks?.toLocaleString() ?? 0}
      </div>
    </div>
  );
}

function ConsensusBar({ consensus }) {
  const total = consensus.n_chunks || 0;
  if (total === 0) {
    return <div style={{ color: "var(--muted)", fontStyle: "italic" }}>No fully-annotated chunks with transcripts yet.</div>;
  }
  const buckets = ["all_agree", "two_agree", "all_disagree"];
  return (
    <div>
      <div style={{ display: "flex", height: 24, borderRadius: 6, overflow: "hidden", marginBottom: 12 }}>
        {buckets.map((key) => {
          const c = consensus[key]?.count || 0;
          const pct = (c / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={key}
              title={`${CONSENSUS_LABELS[key]}: ${c} (${consensus[key].pct}%)`}
              style={{ width: `${pct}%`, background: CONSENSUS_COLORS[key] }}
            />
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 13 }}>
        {buckets.map((key) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ display: "inline-block", width: 12, height: 12, background: CONSENSUS_COLORS[key], borderRadius: 2 }} />
            <span style={{ color: "var(--text)" }}>{CONSENSUS_LABELS[key]}</span>
            <span style={{ color: "var(--muted)" }}>
              {(consensus[key]?.count || 0).toLocaleString()} ({consensus[key]?.pct ?? 0}%)
            </span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: "var(--muted)" }}>
        Across {total.toLocaleString()} chunks with all three annotators having a non-empty transcript.
        Equality compared after lowercase, punctuation strip, and whitespace collapse.
      </div>
    </div>
  );
}

export default function AgreementSection() {
  const [runs, setRuns] = useState(null); // null = not yet loaded
  const [activeRun, setActiveRun] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(true);

  // Step 1: discover which validation runs exist.
  useEffect(() => {
    let cancelled = false;
    setLoadingRuns(true);
    getValidationRuns()
      .then((d) => {
        if (cancelled) return;
        const list = (d?.runs || []).filter(Boolean);
        setRuns(list);
        // Pick the latest run by default (already sorted reverse on server).
        if (list.length > 0) setActiveRun(list[0]);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || "Failed to list validation runs");
        setRuns([]);
      })
      .finally(() => { if (!cancelled) setLoadingRuns(false); });
    return () => { cancelled = true; };
  }, []);

  // Step 2: fetch metrics whenever the selected run changes.
  useEffect(() => {
    if (!activeRun) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingMetrics(true);
    setError(null);
    getAgreementMetrics({ validationRunId: activeRun })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message || "Failed"); })
      .finally(() => { if (!cancelled) setLoadingMetrics(false); });
    return () => { cancelled = true; };
  }, [activeRun]);

  // Hide the card entirely until we know there's at least one run.
  if (loadingRuns) {
    return (
      <div className="eval-section-card">
        <h3>Inter-Annotator Agreement</h3>
        <p style={{ color: "var(--muted)" }}>Checking for validation runs…</p>
      </div>
    );
  }
  if (!runs || runs.length === 0) {
    return (
      <div className="eval-section-card">
        <h3>Inter-Annotator Agreement</h3>
        <p style={{ color: "var(--muted)", fontSize: 13 }}>
          No validation run tagged yet. Tag a set of chunks with{" "}
          <code>validation_run_id</code> (e.g. via the{" "}
          <code>tag_validation_chunks</code> script) and submit at least one
          annotation against them — they'll appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="eval-section-card" style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
        <h3 style={{ margin: 0 }}>Inter-Annotator Agreement</h3>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--muted)" }}>
          Validation run:
          <select
            className="eval-run-picker"
            value={activeRun || ""}
            onChange={(e) => setActiveRun(e.target.value)}
          >
            {runs.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>
      </div>
      <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 0 }}>
        Fleiss' Kappa per categorical field across chunks in this run rated by
        all {data?.raters_per_chunk ?? "k"} annotators. Coloured by Landis &
        Koch bands (poor → almost perfect). Higher κ = stronger consensus.
      </p>

      {loadingMetrics && <p style={{ color: "var(--muted)" }}>Computing…</p>}
      {error && <div className="eval-banner error">Error: {error}</div>}

      {!loadingMetrics && !error && data && (
        <>
          <div style={{ marginTop: 16 }}>
            {Object.entries(data.categorical_agreement || {}).map(([field, info]) => (
              <KappaBar key={field} field={field} info={info} />
            ))}
          </div>

          <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--border, rgba(255,255,255,0.08))" }}>
            <h4 style={{ marginTop: 0, marginBottom: 8 }}>Transcript consensus</h4>
            <ConsensusBar consensus={data.transcript_consensus || {}} />
          </div>

          {data.chunk_coverage && (
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--border, rgba(255,255,255,0.08))" }}>
              <h4 style={{ marginTop: 0, marginBottom: 8 }}>Chunk coverage in this validation run</h4>
              <CoverageTable coverage={data.chunk_coverage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

const COVERAGE_ROWS = [
  { key: "fully_completed",      label: "Fully completed (3 annotators done)", color: "#16a34a" },
  { key: "two_done_one_skipped", label: "2 done, 1 skipped",                   color: "#84cc16" },
  { key: "one_done_two_skipped", label: "1 done, 2 skipped",                   color: "#f59e0b" },
  { key: "all_three_skipped",    label: "All 3 annotators skipped",            color: "#dc2626" },
  { key: "in_progress",          label: "In progress (partial coverage)",      color: "#3b82f6" },
  { key: "untouched",            label: "Not yet started",                     color: "#6b7280" },
];

function CoverageTable({ coverage }) {
  const total = coverage?.total || 0;
  const buckets = coverage?.buckets || {};
  if (total === 0) {
    return <div style={{ color: "var(--muted)", fontStyle: "italic" }}>No chunks in this validation run.</div>;
  }
  return (
    <div>
      <div style={{ display: "flex", height: 24, borderRadius: 6, overflow: "hidden", marginBottom: 12 }}>
        {COVERAGE_ROWS.map(({ key, color }) => {
          const c = buckets[key]?.count || 0;
          const pct = total ? (c / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={key}
              title={`${c.toLocaleString()} chunks (${pct.toFixed(1)}%)`}
              style={{ width: `${pct}%`, background: color }}
            />
          );
        })}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <tbody>
          {COVERAGE_ROWS.map(({ key, label, color }) => {
            const c = buckets[key]?.count || 0;
            const pct = buckets[key]?.pct || 0;
            return (
              <tr key={key} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                <td style={{ padding: "6px 8px", width: 14 }}>
                  <span style={{ display: "inline-block", width: 10, height: 10, background: color, borderRadius: 2 }} />
                </td>
                <td style={{ padding: "6px 8px", color: "var(--text)" }}>{label}</td>
                <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--text)" }}>
                  {c.toLocaleString()}
                </td>
                <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--muted)", width: 60 }}>
                  {pct}%
                </td>
              </tr>
            );
          })}
          <tr style={{ borderTop: "2px solid rgba(255,255,255,0.12)" }}>
            <td></td>
            <td style={{ padding: "8px", fontWeight: 600 }}>Total</td>
            <td style={{ padding: "8px", textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
              {total.toLocaleString()}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
